<template>
  <div data-name="ResponsibilityCardMenu">
    <HorizontalMenu
      :items="menuItems"
      size="small"
      :bottomLine="false"
      :fixedTabs="false"
      :value="selectedTab"
      @onChange="onTabChange"
      @onSameTabClick="onTabChange"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import HorizontalMenu from '@components/HorizontalMenu/HorizontalMenu.vue';

const props = defineProps({
  checklist_count: {
    type: Number,
    default: 0,
    validator: value => value >= 0
  },
  badge_holders_count: {
    type: Number,
    default: 0,
    validator: value => value >= 0
  },
  assignments_count: {
    type: Number,
    default: 0,
    validator: value => value >= 0
  },
  attached_roles_count: {
    type: Number,
    default: 0,
    validator: value => value >= 0
  },
  selectedTab: {
    type: Number,
    default: 0,
    validator: value => value >= 0
  },
  has_context: {
    type: Boolean,
    default: false
  },
  /** When false, the Context + badge is hidden until details are loaded (prevents a one-frame flash). Legacy path only. */
  purposeDetailsLoaded: {
    type: Boolean,
    default: false
  },
  /** idea-MDP-523: when true, Context tab uses numeric count instead of "+" badge. */
  show_context_count_badge: {
    type: Boolean,
    default: false
  },
  context_files_count: {
    type: Number,
    default: 0,
    validator: value => value >= 0
  },
  /** idea-MDP-464 — when false, "Attachment to Roles" tab is hidden (LaunchDarkly in parent). */
  showAttachedRolesTab: {
    type: Boolean,
    default: true,
  },
  /** context-tab-responsibility-page — when false, Context tab is hidden (LaunchDarkly in parent). */
  showContextTab: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["tab-change"]);

function onTabChange(index) {
  emit('tab-change', index);
}

const menuItems = computed(() => {
  const checklistCount = (props.checklist_count === '' || props.checklist_count === null || props.checklist_count === undefined) 
    ? 0 
    : Math.abs(Number(props.checklist_count));
  
  const badgeHoldersCount = (props.badge_holders_count === '' || props.badge_holders_count === null || props.badge_holders_count === undefined) 
    ? 0 
    : Math.abs(Number(props.badge_holders_count));

  const assignmentsCount = (props.assignments_count === '' || props.assignments_count === null || props.assignments_count === undefined) 
    ? 0 
    : Math.abs(Number(props.assignments_count));

  const attachedRolesCount = (props.attached_roles_count === '' || props.attached_roles_count === null || props.attached_roles_count === undefined) 
    ? 0 
    : Math.abs(Number(props.attached_roles_count));

  const filesCount = (props.context_files_count === '' || props.context_files_count === null || props.context_files_count === undefined)
    ? 0
    : Math.abs(Number(props.context_files_count));

  // idea-MDP-523: numeric count = files + 1 if context text is set. Legacy kept inverted "+" when empty.
  const contextItem = props.show_context_count_badge
    ? { title: "Context", count: filesCount + (props.has_context ? 1 : 0) }
    : {
        title: "Context",
        count: null,
        showPlusBadge: props.purposeDetailsLoaded && !props.has_context,
      };
  const defaultsItem = { title: "Expiration Defaults", count: null };

  const items = [
    { title: "Checklist", count: checklistCount },
    { title: "Badge Holders", count: badgeHoldersCount },
    { title: "Assignments", count: assignmentsCount },
  ];
  if (props.showAttachedRolesTab) {
    items.push({ title: "Attachment to Roles", count: attachedRolesCount });
  }
  if (props.showContextTab) {
    items.push(contextItem);
  }
  items.push(defaultsItem);
  return items;
});
</script>